import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { TabContent, TabPane } from 'reactstrap';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components 
import HolidayRow from './HolidayRow';
import withLocalization from '~/hoc/withLocalization';

//Elements
import LoadingSpinner from '../../elements/LoadingSpinner';
import Button from '../../components/CustomButton/CustomButton'
import Select from '../../elements/Select';

//Utils
import { generateYearOptions } from '../../utils/timeCalc';
import { countries } from '../../utils/constants';
import { transformHolidayData } from '../../utils/commonMethods';


class HolidayRecommendationModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            country: null,
            allHolidaysData: {},
            loading: false,
            activeTab: new Date().getFullYear().toString(),
            hasUnsavedChanges: false,
            isTabRendering: false,
        };
        this.years = generateYearOptions();
    }

    handleTabChange = (tab) => {
        const { t } = this.props;
        if (this.state.activeTab === tab) return;

        if (this.state.hasUnsavedChanges) {
            confirmAlert({
                title: t('Do you want to save the changes') + '?',
                message: t("Changes are not saved yet and hence won't reflect, do you wish to save them"),
                buttons: [
                    {
                        label: t('Save'),
                        onClick: async () => {
                            this.setState({ isTabRendering: true });
                            await this.handleSave();
                            this.setState({ activeTab: tab, hasUnsavedChanges: false, isTabRendering: false });
                        },
                    },
                    {
                        label: t('Discard'),
                        onClick: () => {
                            this.setState({ isTabRendering: true }, async () => {
                                await this.loadHolidayDetails();
                                this.setState({ activeTab: tab, hasUnsavedChanges: false, isTabRendering: false });
                            });
                        },
                    },
                ],
            });
        } else {
            this.setState({ activeTab: tab });
        }
    };

    loadHolidayDetails = async () => {
        const { clientStore, t, commonStore } = this.props;
        const { country } = this.state;
        if (!country) return;

        this.setState({ loading: true });

        try {
            const response = await clientStore.getRecommendedHolidays({ country: country.flag });
            const transformedData = {};

            this.years.forEach((year) => {
                const yearData = response?.data?.[year];
                transformedData[year] = {
                    holidays: yearData?.holidays ? transformHolidayData(yearData.holidays) : [],
                    masterHolidayId: yearData?.masterHolidayId || null,
                };
            });

            this.setState({ allHolidaysData: transformedData });
        } catch (error) {
            console.error('Error fetching holidays:', error);
            commonStore.addNotification(t('Failed to fetch holidays. Please try again.'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState.country !== this.state.country && this.state.country) {
            this.loadHolidayDetails();
        }
    }

    updateHolidayData = (holidayId, updater) => {
        const { activeTab } = this.state;

        this.setState((prevState) => {
            const currentData = prevState.allHolidaysData[activeTab];
            if (!currentData) return prevState;

            const updatedHolidays = currentData.holidays.map((holiday) =>
                holiday.id === holidayId ? { ...holiday, ...updater(holiday) } : holiday
            );

            return {
                allHolidaysData: {
                    ...prevState.allHolidaysData,
                    [activeTab]: {
                        ...currentData,
                        holidays: updatedHolidays,
                    },
                }
            };
        });
    };

    checkDateConflict = (holidayId) => {
        const { activeTab, allHolidaysData } = this.state;
        const currentHolidays = allHolidaysData[activeTab]?.holidays || [];
        const targetHoliday = currentHolidays.find(h => h.id === holidayId);
        if (!targetHoliday) return null;
        const conflictingHoliday = currentHolidays.find(h =>
            h.id !== holidayId &&
            h.date === targetHoliday.date &&
            h.isSelected
        );
        return conflictingHoliday;
    };

    handleHolidaySelect = (holidayId, checked) => {
        const { t, commonStore } = this.props;
        if (checked) {
            const conflictingHoliday = this.checkDateConflict(holidayId);

            if (conflictingHoliday) {
                commonStore.addNotification(t('This date is already marked as a holiday.') + `: ${conflictingHoliday.custom_name || conflictingHoliday.name}`, null, 'error');
                return;
            }
        }
        this.updateHolidayData(holidayId, () => ({ isSelected: checked }));
        this.setState({ hasUnsavedChanges: true });
    };

    handleCustomNameChange = (holidayId, value) => {
        this.updateHolidayData(holidayId, () => ({ custom_name: value }));
        this.setState({ hasUnsavedChanges: true });
    };

    getCurrentHolidays = () => {
        const { activeTab, allHolidaysData } = this.state;
        return allHolidaysData[activeTab]?.holidays || [];
    };

    handleFetchHolidays = async () => {
        const { clientStore, t, commonStore } = this.props;
        const { activeTab } = this.state;
        const { country } = this.state;
        if (!country) return;
        const { flag } = country;

        this.setState({ loading: true });

        try {
            await clientStore.getHolidayList({ country: flag, year: activeTab });
            await this.loadHolidayDetails();
            commonStore.addNotification(t('Holiday data fetched successfully'), null, 'success');
        } catch (error) {
            commonStore.addNotification(t('Failed to fetch holidays. Please try again'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleSave = async () => {
        const { clientStore, t, commonStore } = this.props;
        const { activeTab, allHolidaysData, country } = this.state;
        if (!country) return;

        const currentData = allHolidaysData[activeTab];
        if (!currentData) return;

        const selectedHolidays = currentData.holidays.filter((holiday) => holiday.isSelected)

        if (selectedHolidays.length === 0) {
            commonStore.addNotification(t('Please select at least one holiday to save'), null, 'warning');
            return;
        }

        const masterHolidayId = currentData.masterHolidayId || null;

        this.setState({ loading: true });
        try {
            const data = {
                country: country.flag,
                year: parseInt(activeTab),
                holidays: {
                    selectedHolidays, masterHolidayId
                }
            };
            await clientStore.saveRecommendedHolidays(data);
            commonStore.addNotification(t('Holidays saved successfully'), null, 'success');
            this.setState({ hasUnsavedChanges: false })
            await this.loadHolidayDetails();
        } catch (error) {
            let errorMessage = t('Failed to save holidays. Please try again.');
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            commonStore.addNotification(errorMessage, null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    }

    render() {
        const { t, commonStore } = this.props;
        const { activeTab, loading, isTabRendering } = this.state;
        const currentHolidays = this.getCurrentHolidays();
        const hasDataForTab = currentHolidays.length > 0;

        const showContent = !loading && !isTabRendering && hasDataForTab;
        const showEmptyState = !loading && !isTabRendering && !hasDataForTab;
        const showSpinner = loading || isTabRendering;

        return (
            <div className="control-panel">
                <Row>
                    <Col md={5}>
                        <label className="control-label">{t('Country')}</label>
                        <Select
                            options={countries}
                            value={this.state.country}
                            onChange={(option) => this.setState({ country: option })}
                            placeholder={t('Select Country')}
                            isSearchable
                        />
                    </Col>
                    <Col md={7}>
                        <label className="control-label d-block">{t('Year')}</label>
                        <ul className="nav nav-tabs">
                            {this.years.map((year) => (
                                <li className="nav-item" key={year}>
                                    <button
                                        className={`nav-link${activeTab === year ? ' active' : ''}`}
                                        onClick={() => this.handleTabChange(year)}
                                    >
                                        {year}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </Col>
                </Row>
                <TabContent activeTab={activeTab} className="mt-3" key={commonStore.locale}>
                    {this.years.map((year) => (
                        <TabPane tabId={year} key={year} className="p-0">
                            <Row>
                                <Col sm={12}>
                                    {showSpinner && activeTab === year && <LoadingSpinner />}
                                    {showContent && activeTab === year && (
                                        <div className="projectMemberForm">
                                            <table className="table table-hover projectMembersFormTable">
                                                <thead>
                                                    <tr className="table-container-header text-center">
                                                        <th style={{ width: '8%', textAlign: 'center' }}>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id="select-holiday-tooltip">
                                                                        {t('Enable to include holiday in your system')}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span>
                                                                    {t('Enable')} <i className="fa fa-info-circle" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </th>
                                                        <th style={{ width: '18%', textAlign: 'left' }}>
                                                            {t('Holiday Name')}
                                                        </th>
                                                        <th style={{ width: '12%', textAlign: 'left' }}>{t('Date')}</th>
                                                        <th style={{ width: '10%', textAlign: 'left' }}>{t('Type')}</th>
                                                        <th style={{ width: '18%' }}>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id="display-name-tooltip">
                                                                        {t(
                                                                            'You can set a custom display name for the holiday'
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span>
                                                                    {t('Display Name')}{' '}
                                                                    <i className="fa fa-info-circle" />
                                                                </span>
                                                            </OverlayTrigger>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentHolidays.map((holiday) => (
                                                        <HolidayRow
                                                            key={holiday.id}
                                                            holiday={holiday}
                                                            onHolidaySelect={this.handleHolidaySelect}
                                                            onCustomNameChange={this.handleCustomNameChange}
                                                            showAdvancedColumns={false}
                                                            t={t}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {this.state.country && showEmptyState && activeTab === year && (
                                        <div className="text-center mt-3">
                                            <div className='mb-2'>{t("No holidays found for the selected country. Click 'Fetch Holidays' to load the list.")}</div>
                                            <Button
                                                color="primary"
                                                onClick={this.handleFetchHolidays}
                                                disabled={loading}
                                                fill
                                            >
                                                {loading ? <LoadingSpinner /> : t('Fetch Holiday')}
                                            </Button>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </TabPane>
                    ))}
                </TabContent>
                {this.state.country && currentHolidays.length > 0 && (
                    <div className="d-flex justify-content-end mt-3">
                        <Button color="primary" onClick={this.handleSave} disabled={loading || isTabRendering}>
                            {loading ? (
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            ) : (
                                t('Save Holidays')
                            )}
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

export default inject('clientStore', 'commonStore')(withLocalization(observer(HolidayRecommendationModal)));
